import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type ItemType } from "@jasper/client-models";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  itemPreferencesToInput,
  type UpdateProductArgs,
  type useOrderConfiguration,
} from "@jasper/use-cases";
import type { ToothFormat } from "@jasper/forms";
import type { BulkGroup } from "../bulkTeethPlan";
import {
  getReassignments,
  planTeethSelection,
  resolveBulkGroup,
} from "../bulkTeethPlan";
import { getItemTypeForProductType } from "../registry";
import { BULK_PRODUCTS_QUERY_PARAM, buildProductPath } from "../paths";
import type {
  BulkTeethSubmitOptions,
  ConfigurableProduct,
  StepBulkGroup,
} from "../steps/types";

interface Props {
  orderId: string;
  leaderProduct: ConfigurableProduct;
  bulkGroup: StepBulkGroup;
  itemType: ItemType;
  orderConfiguration: ReturnType<typeof useOrderConfiguration>;
}

type NormalizedAssignment = {
  teeth: number[];
  product: ConfigurableProduct | null;
  targetItemType: ItemType;
  targetProductType: ConfigurableProduct["type"];
};

export const useBulkTeethSelection = ({
  leaderProduct,
  bulkGroup,
  itemType,
  orderConfiguration,
}: Props) => {
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "bulk",
  });
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { addOrderProducts, updateProducts, removeOrderProducts, order } =
    orderConfiguration;

  const { groupProducts } = bulkGroup;

  const applyBulk = ({
    survivingGroup,
  }: {
    survivingGroup: BulkGroup | null;
  }) => {
    if (!survivingGroup) {
      return;
    }

    if (survivingGroup.leaderProductId !== leaderProduct.id) {
      navigate(
        buildProductPath(
          orderConfiguration.order!.id,
          survivingGroup.leaderProductId,
          survivingGroup.bulkProductFollowerIds
        )
      );
    } else {
      setSearchParams(prev => ({
        ...prev,
        [BULK_PRODUCTS_QUERY_PARAM]:
          survivingGroup.bulkProductFollowerIds.join(","),
      }));
    }
  };

  const applyTeethSelection = async <T extends ToothFormat>(
    selectedTeethGroups: T[][],
    formatTeethSelection: (args: {
      teeth: T[];
      product: ConfigurableProduct;
      itemType: ItemType;
    }) => Promise<UpdateProductArgs> | UpdateProductArgs,
    submitOptions?: BulkTeethSubmitOptions
  ) => {
    const initialTeeth = selectedTeethGroups.flat();
    const initialPlan = planTeethSelection({
      selectedTeethGroups: selectedTeethGroups.map(group =>
        group.map(tooth => (typeof tooth === "number" ? tooth : tooth.tooth))
      ),
      products: groupProducts,
    });

    const strategy = submitOptions?.mutationStrategy;
    const forcedProductsToDeleteById = new Map<string, ConfigurableProduct>();

    const normalizedAssignments: NormalizedAssignment[] =
      initialPlan.assignments.map(assignment => {
        const targetProductType = strategy
          ? strategy.getTargetProductType(assignment.teeth)
          : leaderProduct.type;
        const targetItemType = getItemTypeForProductType(targetProductType);

        if (!assignment.product) {
          return {
            teeth: assignment.teeth,
            product: null,
            targetItemType,
            targetProductType,
          };
        }

        const canReuseProduct = strategy?.canReuseProduct
          ? strategy.canReuseProduct({
              currentProductType: assignment.product.type,
              targetProductType,
            })
          : true;

        if (!canReuseProduct) {
          forcedProductsToDeleteById.set(
            assignment.product.id,
            assignment.product
          );
          return {
            teeth: assignment.teeth,
            product: null,
            targetItemType,
            targetProductType,
          };
        }

        return {
          teeth: assignment.teeth,
          product: assignment.product,
          targetItemType,
          targetProductType,
        };
      });

    const plan = {
      assignments: normalizedAssignments.map(({ teeth, product }) => ({
        teeth,
        product,
      })),
      productsToDelete: [
        ...initialPlan.productsToDelete,
        ...forcedProductsToDeleteById.values(),
      ],
    };

    const assignmentsToCreate = normalizedAssignments.filter(
      assignment => !assignment.product
    );
    const { productsToDelete } = plan;

    const inheritedItemPreferences = itemPreferencesToInput(
      leaderProduct.itemPreferences
    );
    const knownProductIds = new Set(
      (order?.products ?? []).map(product => product.id)
    );

    let createdProducts: { id: string; teeth: ReadonlyArray<number> }[] = [];

    try {
      if (assignmentsToCreate.length) {
        const productToCreate = await Promise.all(
          assignmentsToCreate.map(async assignment => {
            const update = await formatTeethSelection({
              teeth: assignment.teeth.map(tooth =>
                initialTeeth.find(initialTooth =>
                  typeof initialTooth === "number"
                    ? initialTooth === tooth
                    : initialTooth.tooth === tooth
                )!
              ),
              product: leaderProduct,
              itemType: assignment.targetItemType,
            });

            return {
              type: assignment.targetProductType,
              teeth: assignment.teeth,
              items:
                update.items?.map(item => ({
                  ...item,
                })) ?? [],
            };
          })
        );

        const result = await addOrderProducts(productToCreate);

        createdProducts = (result.data?.addOrderProducts.products ?? []).filter(
          product => !knownProductIds.has(product.id)
        );
      }

      const targetAssignmentByProductId = new Map(
        normalizedAssignments
          .filter(
            (
              assignment
            ): assignment is NormalizedAssignment & {
              product: ConfigurableProduct;
            } => !!assignment.product
          )
          .map(assignment => [assignment.product.id, assignment])
      );

      // Teeth carrying a value (e.g. implanted / pontic) can change without moving,
      // so every kept product must be saved again, not only the moved ones.
      const hasTeethValues = initialTeeth.some(
        tooth => typeof tooth !== "number"
      );
      const productsToUpdate = hasTeethValues
        ? plan.assignments.filter(
            (
              assignment
            ): assignment is {
              teeth: number[];
              product: ConfigurableProduct;
            } => !!assignment.product
          )
        : getReassignments(plan);

      const updates = await Promise.all([
        ...productsToUpdate.map(({ product, teeth }) => {
          const assignment = targetAssignmentByProductId.get(product.id);
          const targetItemType = assignment?.targetItemType ?? itemType;

          return formatTeethSelection({
            teeth: teeth.map(tooth =>
              initialTeeth.find(initialTooth =>
                typeof initialTooth === "number"
                  ? initialTooth === tooth
                  : initialTooth.tooth === tooth
              )!
            ),
            product,
            itemType: targetItemType,
          });
        }),
        ...(inheritedItemPreferences
          ? createdProducts.map(({ id }) => ({
              productId: id,
              itemPreferences: inheritedItemPreferences,
            }))
          : []),
      ]);

      if (updates.length) {
        await updateProducts(updates);
      }

      if (productsToDelete.length) {
        await removeOrderProducts(productsToDelete.map(({ id }) => id));
      }
    } catch {
      notifications.show({
        title: t("errorNotificationTitle"),
        message: t("errorNotificationMessage"),
        color: "red",
      });
      return;
    }

    const survivingGroup = resolveBulkGroup({
      plan,
      leaderProductId: leaderProduct.id,
      createdProducts,
    });

    applyBulk({ survivingGroup });
  };

  return { applyTeethSelection };
};
